import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { ViewModelTagOperation } from '@view-model/domain/view-model/ViewModelTagOperation';
import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  groupId: string;
  viewModelTagId: string;
  tagName: string;
  setTagName: React.Dispatch<React.SetStateAction<string>>;
};

export const ViewModelTagRenameModal: React.FC<Props> = ({
  isOpen,
  onClose,
  groupId,
  viewModelTagId,
  tagName,
  setTagName,
}: Props) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [tagNameTemp, setTagNameTemp] = useState(tagName);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const updatedName = await ViewModelTagOperation.updateName(groupId, viewModelTagId, tagNameTemp);
      if (updatedName) {
        toast.success(t('home.tag.renameModal.renameSuccess'));
        setTagName(updatedName);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  }, [groupId, onClose, setTagName, tagNameTemp, viewModelTagId, t]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTagNameTemp(e.target.value);
    },
    [setTagNameTemp]
  );

  const canSave = tagNameTemp.trim() !== '';

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('home.tag.renameModal.title')}
      submitButton={
        <Button color="primary" onClick={handleSubmit} disabled={!canSave} loading={isSubmitting}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color={'secondary'} onClick={onClose} disabled={isSubmitting}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="overflow-y-auto p-4" style={{ width: '600px', maxHeight: '70vh' }}>
        <div className="flex justify-between">
          <div className="w-full">
            <div className="text-lg font-bold">{t('home.tag.renameModal.nameLabel')}</div>
            <Input
              type="text"
              placeholder={t('home.tag.renameModal.namePlaceholder')}
              value={tagNameTemp}
              onChange={handleChange}
              disabled={isSubmitting}
              autoFocus={true}
            />
          </div>
        </div>
      </div>
    </GeneralModal>
  );
};
